import axios from "axios";

// The backend caps `limit` at 100 on the list endpoints, so an export has to
// page through the results rather than asking for everything at once.
export const EXPORT_PAGE_SIZE = 100;

// Safety stop so a bad pagination response can never spin forever.
const MAX_EXPORT_PAGES = 500;

/**
 * Fetches records from a paginated list endpoint by looping pages.
 *
 * With no range options this walks the whole result set at EXPORT_PAGE_SIZE.
 * Pass fromPage/toPage/pageSize to export only part of it — the page numbers
 * then mean pages as the table shows them, so pageSize must be the table's
 * rows-per-page.
 *
 * @param {Object} options
 * @param {Function} options.buildRequest (page, limit) => ({ url, requestData })
 * @param {Function} [options.onProgress] called with { fetched, totalCount } after each page
 * @param {Function} [options.isCancelled] return true to abort the loop early
 * @param {number}   [options.fromPage=1] first page to fetch (1-based)
 * @param {number}   [options.toPage]     last page to fetch; defaults to the last page
 * @param {number}   [options.pageSize]   rows per request
 * @returns {Promise<Array<Object>>}
 */
export const fetchAllPaginated = async ({
  buildRequest,
  onProgress,
  isCancelled,
  fromPage = 1,
  toPage = null,
  pageSize = EXPORT_PAGE_SIZE,
}) => {
  const records = [];
  const startPage = Math.max(1, fromPage);
  let page = startPage;
  let lastPage = toPage || Infinity;
  // Count requests, not the page number — a range starting past MAX_EXPORT_PAGES
  // must still be fetchable.
  let fetches = 0;

  while (page <= lastPage && fetches < MAX_EXPORT_PAGES) {
    if (isCancelled && isCancelled()) return records;

    const { url, requestData } = buildRequest(page, pageSize);

    const response = await axios.post(url, requestData, {
      headers: { "Content-Type": "application/json" },
    });

    if (response.data.success !== true) {
      throw new Error(response.data.message || "Failed to fetch data");
    }

    const { data, pagination } = response.data;
    records.push(...(data || []));
    fetches += 1;

    // Never walk past the real end of the list, even if toPage asked us to.
    const totalPages = pagination?.totalPages || 1;
    lastPage = Math.min(toPage || totalPages, totalPages);

    if (onProgress) {
      onProgress({
        fetched: records.length,
        totalCount: expectedRowCount({
          fromPage: startPage,
          lastPage,
          pageSize,
          totalCount: pagination?.totalCount,
          fallback: records.length,
        }),
      });
    }

    // Nothing came back — stop rather than requesting the same page forever.
    if (!data || data.length === 0) break;

    page += 1;
  }

  return records;
};

/**
 * How many rows the selected page range should yield — used as the progress
 * denominator so a 2-page export reads "40 of 40", not "40 of 900".
 */
const expectedRowCount = ({ fromPage, lastPage, pageSize, totalCount, fallback }) => {
  if (!totalCount && totalCount !== 0) return fallback;

  const skipped = (fromPage - 1) * pageSize;
  const remaining = totalCount - skipped;
  const rangeCapacity = (lastPage - fromPage + 1) * pageSize;

  return Math.max(0, Math.min(remaining, rangeCapacity));
};

const slugify = (value) =>
  value
    .toString()
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

/**
 * Builds an export filename as `<prefix>-<part>-...-<YYYY-MM-DD>.csv`.
 * Empty / nullish parts are dropped.
 */
export const buildExportFilename = (prefix, parts = []) => {
  const today = new Date().toISOString().split("T")[0];

  const slugged = parts
    .filter((part) => part !== null && part !== undefined && String(part).trim() !== "")
    .map(slugify)
    .filter(Boolean);

  return `${[prefix, ...slugged, today].join("-")}.csv`;
};
